/**
 * ShippingPicker — country selector + shipping method dropdown.
 *
 * Mounted on the gemstone detail page above the Buy buttons. Auto-detects the
 * visitor's country via `/api/geo/detect` (falls back to ID), then fetches
 * available methods from `/api/shipping/quote` whenever the country OR
 * the price changes.
 *
 * Communicates the chosen method up via `onChange({ method, country, costUsd })`
 * so the parent page can roll the cost into Stripe / PayPal / Bank Transfer
 * checkout payloads.
 *
 * Hides itself when the product is digital (`physical=false`) — caller passes
 * `physical={gem.physical}` for that case.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import TX, { useTX } from "./TX";

// Compact, human-friendly country list keyed by zone. We don't ship every
// ISO-3166 country in the picker (that's overwhelming) — instead we show the
// top markets per zone + a free-text "other" entry that defaults to ROW.
const COUNTRY_GROUPS = [
  { label: "Indonesia",       items: [["ID", "Indonesia"]] },
  { label: "Asia",            items: [
    ["SG", "Singapore"], ["MY", "Malaysia"], ["TH", "Thailand"], ["PH", "Philippines"],
    ["VN", "Vietnam"], ["HK", "Hong Kong"], ["TW", "Taiwan"], ["JP", "Japan"],
    ["KR", "South Korea"], ["CN", "China"], ["IN", "India"], ["AE", "UAE"], ["SA", "Saudi Arabia"],
  ]},
  { label: "USA & Canada",    items: [["US", "United States"], ["CA", "Canada"]] },
  { label: "Europe",          items: [
    ["GB", "United Kingdom"], ["DE", "Germany"], ["FR", "France"], ["IT", "Italy"], ["ES", "Spain"],
    ["NL", "Netherlands"], ["CH", "Switzerland"], ["BE", "Belgium"], ["AT", "Austria"],
    ["SE", "Sweden"], ["NO", "Norway"], ["DK", "Denmark"], ["IE", "Ireland"], ["PL", "Poland"], ["RU", "Russia"],
  ]},
  { label: "Oceania",         items: [["AU", "Australia"], ["NZ", "New Zealand"]] },
  { label: "Rest of World",   items: [["ZZ", "Other (Rest of World)"]] },
];

const ShippingPicker = ({ subtotalUsd, physical = true, onChange }) => {
  const { format, convert, currency } = useCurrency();
  const t = useTX();
  const [country, setCountry] = useState(localStorage.getItem("opik_ship_country") || "");
  const [methods, setMethods] = useState([]);
  const [methodKey, setMethodKey] = useState("");
  const [loading, setLoading] = useState(false);

  // Geo-detect on first mount so the dropdown defaults to the visitor's
  // country. Only runs if no saved selection.
  useEffect(() => {
    if (country) return;
    api.get("/geo/detect").then(({ data }) => {
      const cc = (data?.country || "").toUpperCase();
      if (cc) {
        setCountry(cc);
        localStorage.setItem("opik_ship_country", cc);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch methods whenever country OR subtotal changes (so the "Free above
  // $500" toggle reacts to variation price changes too).
  useEffect(() => {
    if (!physical || !country) {
      setMethods([]); setMethodKey("");
      onChange?.({ method: null, country, costUsd: 0 });
      return;
    }
    setLoading(true);
    const cc = country === "ZZ" ? "XX" : country;  // ZZ = our "Other" placeholder
    api.get(`/shipping/quote?country=${encodeURIComponent(cc)}&subtotal_usd=${subtotalUsd}`)
      .then(({ data }) => {
        const list = data?.methods || [];
        setMethods(list);
        // Auto-select cheapest method (or first) on country change.
        const cheapest = list.slice().sort((a, b) => a.cost_usd - b.cost_usd)[0];
        const next = cheapest?.key || "";
        setMethodKey(next);
        onChange?.({
          method: cheapest || null,
          country: cc,
          costUsd: cheapest ? cheapest.cost_usd : 0,
        });
      }).catch(() => { setMethods([]); setMethodKey(""); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, subtotalUsd, physical]);

  const onMethodChange = (e) => {
    const k = e.target.value;
    setMethodKey(k);
    const m = methods.find((x) => x.key === k);
    onChange?.({ method: m || null, country, costUsd: m ? m.cost_usd : 0 });
  };

  const selected = useMemo(() => methods.find((m) => m.key === methodKey), [methods, methodKey]);

  if (!physical) return null;

  return (
    <div data-testid="shipping-picker" className="border border-white/10 bg-onyx p-5 mb-8">
      <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-4"><TX>Shipping</TX></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block font-accent text-[10px] tracking-[0.22em] uppercase text-white/45 mb-2"><TX>Ship to</TX></label>
          <select
            data-testid="shipping-country"
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              localStorage.setItem("opik_ship_country", e.target.value);
            }}
            className="w-full bg-transparent border border-white/20 hover:border-gold/40 focus:border-gold focus:outline-none px-3 py-2.5 text-white text-sm"
          >
            <option value="" className="bg-noir">{t("Select your country")}</option>
            {COUNTRY_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label} className="bg-noir">
                {g.items.map(([code, name]) => (
                  <option key={code} value={code} className="bg-noir">{name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-accent text-[10px] tracking-[0.22em] uppercase text-white/45 mb-2"><TX>Method</TX></label>
          {!country ? (
            <div className="text-white/40 text-xs italic py-3"><TX>Select a country first</TX></div>
          ) : loading ? (
            <div className="text-white/40 text-xs py-3"><TX>Loading shipping options…</TX></div>
          ) : methods.length === 0 ? (
            <div data-testid="shipping-unavailable" className="text-amber-400 text-xs py-3 leading-relaxed">
              <TX>Shipping option unavailable for this country.</TX>
            </div>
          ) : (
            <select
              data-testid="shipping-method"
              value={methodKey}
              onChange={onMethodChange}
              className="w-full bg-transparent border border-white/20 hover:border-gold/40 focus:border-gold focus:outline-none px-3 py-2.5 text-white text-sm"
            >
              {methods.map((m) => (
                <option key={m.key} value={m.key} className="bg-noir">
                  {m.label} · {m.free ? "Free" : format(convert(m.cost_usd, currency))} {m.eta ? `(${m.eta})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {selected && (
        <div className="text-white/55 text-xs leading-relaxed flex flex-wrap items-baseline gap-3" data-testid="shipping-summary">
          <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40"><TX>Total Shipping</TX></span>
          <span className="text-ivory text-base font-heading">
            {selected.free ? <TX>Free</TX> : format(convert(selected.cost_usd, currency))}
          </span>
          {selected.eta && <span className="text-white/40 text-xs"><TX>{selected.eta}</TX></span>}
          {selected.free && (
            <span className="font-accent text-[9px] tracking-[0.22em] uppercase bg-gold/20 text-gold px-2 py-0.5 ml-auto">
              <TX>Complimentary over $500</TX>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ShippingPicker;
